import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

/** Admin-only. Bulk-export everyone's logged time as .xlsx. Timesheets have
 * no approval workflow, so unlike Leaves' export there's no status filter. */
export default function TimesheetExportDialog() {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [year, setYear] = useState("all");
  const [userId, setUserId] = useState("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (open) api.get("/users", { params: { kind: "internal" } }).then((r) => setUsers(r.data || [])).catch(() => {});
  }, [open]);

  const runExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (year !== "all") params.year = year;
      if (userId !== "all") params.user_id = userId;
      const resp = await api.get("/timesheet/export", { params, responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet-export-${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      setOpen(false);
    } catch (e) {
      toast.error(`Export failed: ${e?.response?.status || e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" data-testid="timesheet-export-open-btn">
          <Download size={12} className="mr-1.5" />
          Bulk export
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md" data-testid="timesheet-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[#F54800]" /> Export timesheet data
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Downloads every logged time entry as an .xlsx.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="label-overline">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="timesheet-export-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-overline">Employee</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="timesheet-export-employee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={runExport} disabled={exporting} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="timesheet-export-run-btn">
            <Download size={13} className="mr-1.5" /> {exporting ? "Exporting…" : "Download .xlsx"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
